import request from '@/config/axios'
import type { PageParam } from '@/types/common'

/** 后端统一分页响应结构。 */
export interface PageResult<T> {
  list: T[]
  total: number
}

// ==================== 律师准入 ====================

export interface LawyerVO {
  id: number
  userId: number
  name: string
  certNo: string
  lawFirm?: string
  firmId?: number
  firmName?: string
  phone?: string
  avatar?: string
  city?: string
  practiceType?: string
  specialities?: string[]
  score?: number
  creditScore?: number
  caseSuccessCount?: number
  dailyLimit?: number
  todayGrabCount?: number
  /** 律师自定案件服务费(元) */
  caseServiceFee?: number
  supplierType?: string
  availableBalance?: number
  verifyStatus?: string
  enabled?: boolean
  verifyTime?: string
  createTime?: string
}

export interface LawyerPageReqVO extends PageParam {
  name?: string
  verifyStatus?: string
  lawFirm?: string
  city?: string
  firmId?: number
}

export interface LawyerSaveReqVO {
  id?: number
  userId: number
  name: string
  certNo: string
  lawFirm?: string
  phone?: string
  avatar?: string
  city?: string
  practiceType?: string
  specialities?: string[]
  caseServiceFee?: number
  budget?: number
}

/** 管理端代建：手机号+初始密码，直接通过并可接单 */
export interface LawyerCreateDirectReqVO {
  mobile: string
  password: string
  name: string
  certNo: string
  lawFirm?: string
  firmId?: number
  city?: string
  practiceType?: string
  specialities?: string[]
  caseServiceFee?: number
}

export interface LawyerVerifyReqVO {
  id: number
  pass: boolean
  certVerifyStatus?: string
  faceSimilarity?: number
  comment?: string
}

export interface LawyerVerifyLogVO {
  id: number
  lawyerId: number
  certVerifyStatus?: string
  faceSimilarity?: number
  verifyResult?: string
  comment?: string
  verifyTime?: string
}

export interface DashboardStatsVO {
  paidOrderCountMonth?: number
  gmvMonth?: number
  pendingOpportunityCount?: number
  activeLawyerCount?: number
  /** PLATFORM=全租户；SELF=官方本所 */
  scopeMode?: string
  scopeFirmId?: number
  todo?: {
    lawyerPendingVerify?: number
    settleAppliedAmount?: number
    withdrawPending?: number
    complaintPending?: number
  }
}

export const LawyerApi = {
  /** 律师分页 */
  page: async (params: LawyerPageReqVO): Promise<PageResult<LawyerVO>> =>
    request.get({ url: '/lawyer/lawyer/page', params }),
  /** 律师详情 */
  get: async (id: number): Promise<LawyerVO> =>
    request.get({ url: '/lawyer/lawyer/get', params: { id } }),
  /** 按登录用户查询律师档案（律师工作台用） */
  getByUserId: async (userId: number): Promise<LawyerVO> =>
    request.get({ url: '/lawyer/lawyer/get-by-user-id', params: { userId } }),
  /** 代建律师（手机号+初始密码，直接通过并可接单） */
  create: async (data: LawyerCreateDirectReqVO): Promise<number> =>
    request.post({ url: '/lawyer/lawyer/create', data }),
  /** 更新律师 */
  update: async (data: LawyerSaveReqVO): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/update', data }),
  /** 准入审核（通过/驳回） */
  verify: async (data: LawyerVerifyReqVO): Promise<boolean> =>
    request.post({ url: '/lawyer/lawyer/verify', data }),
  /** 启用/禁用 */
  updateEnabled: async (id: number, enabled: boolean): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/enabled', params: { id, enabled } }),
  /** 修改日接单上限 */
  updateDailyLimit: async (data: { id: number; dailyLimit: number }): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/daily-limit', data }),
  /** 修改律师评分（0～5） */
  updateScore: async (data: { id: number; score: number }): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/score', data }),
  /** 改挂入驻律所 */
  updateFirmId: async (id: number, firmId: number): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/firm-id', params: { id, firmId } }),
  /** 更新头像 */
  updateAvatar: async (id: number, avatar: string): Promise<boolean> =>
    request.put({ url: '/lawyer/lawyer/avatar', params: { id, avatar } }),
  /** 永久禁用 */
  disable: async (data: { id: number; comment: string }): Promise<boolean> =>
    request.post({ url: '/lawyer/lawyer/disable', data }),
  /** 审核历史 */
  verifyLogs: async (lawyerId: number): Promise<LawyerVerifyLogVO[]> =>
    request.get({ url: '/lawyer/lawyer/verify-logs', params: { lawyerId } }),
  /** 待审核数量 */
  pendingCount: async (): Promise<number> =>
    request.get({ url: '/lawyer/lawyer/pending-count' }),
  /** 删除 */
  delete: async (id: number): Promise<boolean> =>
    request.delete({ url: '/lawyer/lawyer/delete', params: { id } })
}

export const DashboardApi = {
  stats: async (): Promise<DashboardStatsVO> =>
    request.get({ url: '/lawyer/dashboard/stats' })
}

// ==================== 商机撮合 ====================

export interface OpportunityVO {
  id: number
  oppNo: string
  caseId: number
  userId: number
  /** 会员昵称（管理端回填） */
  userNickname?: string
  /** 会员手机号（管理端回填） */
  userMobile?: string
  causeType?: string
  summary?: string
  region?: string
  budget?: number
  mode?: string
  matchScoreJson?: string
  aiPreprocessJson?: string
  status?: string
  deadlineAt?: string
  grabbedLawyerId?: number
  grabbedLawyerName?: string
  /** 用户已选意向律师；有值则运营不可再分配 */
  preferredLawyerId?: number
  preferredLawyerName?: string
  /** 案件已绑承办律师；有值则不可再分配 */
  caseLawyerId?: number
  /** 支付激活时间；为空=用户已咨询未付款（仍可进抢单大厅） */
  activatedAt?: string
  createTime?: string
}

export interface OpportunityPageReqVO extends PageParam {
  oppNo?: string
  status?: string
  mode?: string
  causeType?: string
  grabbedLawyerId?: number
}

export interface OpportunityCreateReqVO {
  caseId: number
  userId: number
  causeType: string
  summary?: string
  region?: string
  budget?: number
  mode?: string
  aiPreprocessJson?: string
}

export const OpportunityApi = {
  /** 商机分页（管理端） */
  page: async (params: OpportunityPageReqVO): Promise<PageResult<OpportunityVO>> =>
    request.get({ url: '/lawyer/opportunity/page', params }),
  /** 商机详情 */
  get: async (id: number): Promise<OpportunityVO> =>
    request.get({ url: '/lawyer/opportunity/get', params: { id } }),
  /** 生成商机 */
  create: async (data: OpportunityCreateReqVO): Promise<number> =>
    request.post({ url: '/lawyer/opportunity/create', data }),
  /** 抢单（律师端，乐观锁防超卖） */
  grab: async (oppId: number, lawyerId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/grab', params: { oppId, lawyerId } }),
  /** 派单确认（律师端） */
  confirmAssign: async (oppId: number, lawyerId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/confirm-assign', params: { oppId, lawyerId } }),
  /** 商机转入服务中 */
  updateStatusToServing: async (oppId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/serving', params: { oppId } }),
  /** 商机结案 */
  markClosed: async (oppId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/closed', params: { oppId } }),
  /** 取消商机 */
  cancel: async (id: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/cancel', params: { id } }),
  /** 运营分配律师（未付锁意向；已付绑正式案件） */
  adminAssign: async (oppId: number, lawyerId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/opportunity/admin-assign', params: { oppId, lawyerId } }),
  /** 商机大厅可抢列表（律师端） */
  grabableList: async (): Promise<OpportunityVO[]> =>
    request.get({ url: '/lawyer/opportunity/grabable-list' }),
  /** 我的商机（律师端） */
  myList: async (lawyerId: number): Promise<OpportunityVO[]> =>
    request.get({ url: '/lawyer/opportunity/my-list', params: { lawyerId } })
}

// ==================== 订单结算 ====================

export interface OrderVO {
  id: number
  orderNo: string
  caseId: number
  oppId?: number
  userId: number
  lawyerId: number
  type?: string
  payAmount?: number
  commRate?: number
  commAmount?: number
  lawyerAmount?: number
  payStatus?: string
  settleStatus?: string
  settleAt?: string
  createTime?: string
}

export interface OrderPageReqVO extends PageParam {
  orderNo?: string
  lawyerId?: number
  userId?: number
  type?: string
  payStatus?: string
  settleStatus?: string
}

export interface OrderCreateReqVO {
  caseId: number
  oppId?: number
  userId: number
  lawyerId: number
  type: string
  payAmount: number
  commRate?: number
}

export const OrderApi = {
  /** 订单分页（管理端） */
  page: async (params: OrderPageReqVO): Promise<PageResult<OrderVO>> =>
    request.get({ url: '/lawyer/order/page', params }),
  /** 订单详情 */
  get: async (id: number): Promise<OrderVO> =>
    request.get({ url: '/lawyer/order/get', params: { id } }),
  /** 创建订单 */
  create: async (data: OrderCreateReqVO): Promise<number> =>
    request.post({ url: '/lawyer/order/create', data }),
  /** 更新支付状态（支付回调/mock） */
  updatePayStatus: async (orderId: number, payStatus: string): Promise<boolean> =>
    request.post({ url: '/lawyer/order/pay-status', params: { orderId, payStatus } }),
  /** 律师申请结算 */
  applySettle: async (orderId: number, lawyerId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/order/apply-settle', params: { orderId, lawyerId } }),
  /** 平台核验分账（幂等） */
  settle: async (orderId: number): Promise<boolean> =>
    request.post({ url: '/lawyer/order/settle', params: { orderId } }),
  /** 我的订单（律师端收益） */
  myList: async (lawyerId: number): Promise<OrderVO[]> =>
    request.get({ url: '/lawyer/order/my-list', params: { lawyerId } })
}

// ==================== 提现 ====================

export interface WithdrawVO {
  id: number
  withdrawNo: string
  lawyerId: number
  amount?: number
  status?: string
  payType?: string
  accountInfo?: string
  handlerId?: number
  handleTime?: string
  remark?: string
  createTime?: string
}

export interface WithdrawPageReqVO extends PageParam {
  withdrawNo?: string
  lawyerId?: number
  status?: string
}

export interface WithdrawCreateReqVO {
  lawyerId: number
  amount: number
  payType: string
  accountInfo: string
}

export interface WithdrawAuditReqVO {
  id: number
  pass: boolean
  remark?: string
}

export const WithdrawApi = {
  /** 提现分页（管理端） */
  page: async (params: WithdrawPageReqVO): Promise<PageResult<WithdrawVO>> =>
    request.get({ url: '/lawyer/withdraw/page', params }),
  /** 提现详情 */
  get: async (id: number): Promise<WithdrawVO> =>
    request.get({ url: '/lawyer/withdraw/get', params: { id } }),
  /** 申请提现 */
  apply: async (data: WithdrawCreateReqVO): Promise<number> =>
    request.post({ url: '/lawyer/withdraw/apply', data }),
  /** 提现审核 */
  audit: async (data: WithdrawAuditReqVO): Promise<boolean> =>
    request.post({ url: '/lawyer/withdraw/audit', data }),
  /** 我的提现记录（律师端） */
  myList: async (lawyerId: number): Promise<WithdrawVO[]> =>
    request.get({ url: '/lawyer/withdraw/my-list', params: { lawyerId } })
}

// ==================== 案件管理 ====================

export interface CaseVO {
  id: number
  caseNo: string
  userId: number
  conversationId?: number
  opportunityId?: number
  lawyerId?: number
  title: string
  caseType?: string
  description?: string
  status?: string
  source?: string
  priority?: string
  amountClaim?: number
  amountQuote?: number
  province?: string
  city?: string
  district?: string
  address?: string
  partyAName?: string
  partyAPhone?: string
  partyBName?: string
  caseSuccessProbability?: number
  aiAnalysisJson?: string
  acceptTime?: string
  closeTime?: string
  createTime?: string
}

export interface CaseElementVO {
  id: number
  caseId: number
  elementKey: string
  elementName: string
  elementType: string
  elementValue?: string
  options?: string
  required?: boolean
  filled?: boolean
  sort?: number
}

export interface CasePageReqVO extends PageParam {
  caseNo?: string
  title?: string
  userId?: number
  caseType?: string
  status?: string
  lawyerId?: number
  createTime?: string[]
}

export const CaseApi = {
  /** 案件分页 */
  page: async (params: CasePageReqVO): Promise<PageResult<CaseVO>> =>
    request.get({ url: '/lawyer/case/page', params }),
  /** 案件详情 */
  get: async (id: number): Promise<CaseVO> =>
    request.get({ url: '/lawyer/case/get', params: { id } }),
  /** 案件要素列表 */
  elementList: async (caseId: number): Promise<CaseElementVO[]> =>
    request.get({ url: '/lawyer/case/element-list', params: { caseId } }),
  /** 律师受理案件 */
  accept: async (id: number, lawyerId: number): Promise<boolean> =>
    request.put({ url: '/lawyer/case/accept', params: { id, lawyerId } }),
  /** 案件结案 */
  settle: async (id: number): Promise<boolean> =>
    request.put({ url: '/lawyer/case/settle', params: { id } }),
  /** 案件归档 */
  archive: async (id: number): Promise<boolean> =>
    request.put({ url: '/lawyer/case/archive', params: { id } })
}

// ==================== 咨询会话 ====================

export interface ConversationMessageVO {
  id: number
  conversationId: number
  type?: string
  content?: string
  createTime?: string
}

export interface ConversationVO {
  id: number
  userId: number
  lawyerId?: number
  caseId?: number
  title: string
  status?: string
  source?: string
  lastMessage?: string
  lastMessageTime?: string
  messageCount?: number
  createTime?: string
}

export interface ConversationPageReqVO extends PageParam {
  userId?: number
  title?: string
  status?: string
  createTime?: string[]
}

export const ConsultApi = {
  /** 咨询会话分页 */
  page: async (params: ConversationPageReqVO): Promise<PageResult<ConversationVO>> =>
    request.get({ url: '/lawyer/consult/page', params }),
  /** 咨询会话详情 */
  get: async (id: number): Promise<ConversationVO> =>
    request.get({ url: '/lawyer/consult/get', params: { id } }),
  /** 会话消息分页 */
  messagePage: async (conversationId: number, params?: any): Promise<PageResult<ConversationMessageVO>> =>
    request.get({ url: '/lawyer/consult/message-page', params: { conversationId, ...params } })
}

// ==================== 证据管理 ====================

export interface EvidenceVO {
  id: number
  caseId: number
  userId?: number
  name: string
  type?: string
  fileUrl?: string
  fileName?: string
  fileSize?: number
  description?: string
  status?: string
  auditRemark?: string
  createTime?: string
}

export interface EvidencePageReqVO extends PageParam {
  caseId?: number
  userId?: number
  type?: string
  status?: string
  createTime?: string[]
}

export interface EvidenceAuditReqVO {
  id: number
  status: string
  auditRemark?: string
}

export const EvidenceApi = {
  /** 证据分页 */
  page: async (params: EvidencePageReqVO): Promise<PageResult<EvidenceVO>> =>
    request.get({ url: '/lawyer/evidence/page', params }),
  /** 证据审核（采纳/驳回） */
  audit: async (data: EvidenceAuditReqVO): Promise<boolean> =>
    request.put({ url: '/lawyer/evidence/audit', data })
}

// ==================== 文书管理 ====================

export interface DocumentVO {
  id: number
  caseId: number
  docNo: string
  title: string
  docType?: string
  content?: string
  fileUrl?: string
  status?: string
  generatedBy?: string
  generatedTime?: string
  requiredSignCount?: number
  signedCount?: number
  /** 发送给用户时间；空=仅律师可见 */
  sentToUserAt?: string
  createTime?: string
}

export const DocumentApi = {
  /** 文书分页（后台默认列表，含 LLM 草稿） */
  page: async (params: DocumentPageReqVO): Promise<PageResult<DocumentVO>> =>
    request.get({ url: '/lawyer/document/page', params }),
  /** 文书列表（按案件） */
  listByCase: async (caseId: number): Promise<DocumentVO[]> =>
    request.get({ url: '/lawyer/document/list-by-case', params: { caseId } }),
  /** 文书详情 */
  get: async (id: number): Promise<DocumentVO> =>
    request.get({ url: '/lawyer/document/get', params: { id } }),
  /** 文书归档 */
  archive: async (id: number): Promise<boolean> =>
    request.put({ url: '/lawyer/document/archive', params: { id } })
}

export interface DocumentPageReqVO extends PageParam {
  caseId?: number
  caseNo?: string
  docNo?: string
  status?: string
  generatedBy?: string
}

// ==================== 签署记录 ====================

export interface SignVO {
  id: number
  caseId: number
  documentId: number
  userId?: number
  lawyerId?: number
  signRole?: string
  signStatus?: string
  signChannel?: string
  signImageUrl?: string
  signedAt?: string
  rejectReason?: string
  expireAt?: string
  createTime?: string
}

export interface SignPageReqVO extends PageParam {
  caseId?: number
  documentId?: number
  signRole?: string
  signStatus?: string
  createTime?: string[]
}

export const SignApi = {
  /** 签署记录分页 */
  page: async (params: SignPageReqVO): Promise<PageResult<SignVO>> =>
    request.get({ url: '/lawyer/sign/page', params })
}

// ==================== 平台定价（租户配置） ====================

export interface LawyerTenantConfigVO {
  tenantId?: number
  brandName?: string
  bizMode?: string
  bizModeLocked?: boolean
  bizModeLockedAt?: string
  bizModeLockedBy?: string
  allowHotSwitch?: boolean
  allowForceSwitch?: boolean
  features?: Record<string, any>
  canonicalFirmId?: number
  defaultCommRate?: number
  defaultCaseServiceFee?: number
  assignStrategy?: string
  dutyLawyerId?: number
}

export interface LawyerBizModePrecheckVO {
  assignedServingOppCount?: number
  paidOpenOrderCount?: number
  canSwitchSafe?: boolean
  allowForceSwitch?: boolean
  bizModeLocked?: boolean
  currentBizMode?: string
}

export const TenantConfigApi = {
  // 未开通律师的租户无此权限；静默失败，避免侧栏 generateRoutes 弹 Access Denied
  get: async (): Promise<LawyerTenantConfigVO> =>
    request.get({
      url: '/lawyer/tenant-config/get',
      headers: { ignoreError: true }
    }),
  updatePricing: async (defaultCaseServiceFee: number): Promise<boolean> =>
    request.put({ url: '/lawyer/tenant-config/pricing', data: { defaultCaseServiceFee } }),
  updateAssignStrategy: async (data: {
    assignStrategy: string
    dutyLawyerId?: number
  }): Promise<boolean> =>
    request.put({ url: '/lawyer/tenant-config/assign-strategy', data }),
  /** @deprecated 生产禁止；本地 allow-hot-switch 时后端转 change */
  updateBizMode: async (bizMode: string): Promise<boolean> =>
    request.put({ url: '/lawyer/tenant-config/biz-mode', data: { bizMode } }),
  lockBizMode: async (bizMode: string): Promise<boolean> =>
    request.post({ url: '/lawyer/tenant-config/lock-biz-mode', data: { bizMode } }),
  precheckBizMode: async (): Promise<LawyerBizModePrecheckVO> =>
    request.get({ url: '/lawyer/tenant-config/biz-mode/precheck' }),
  changeBizMode: async (data: {
    bizMode: string
    reason: string
    force?: boolean
  }): Promise<boolean> =>
    request.post({ url: '/lawyer/tenant-config/change-biz-mode', data })
}

// ==================== 案件 AI Copilot（通用大模型） ====================

export const AiCopilotApi = {
  analyze: async (caseId: number): Promise<any> =>
    request.post({ url: '/lawyer/ai-copilot/analyze', data: { caseId }, timeout: 180000 }),
  strategy: async (caseId: number): Promise<any> =>
    request.post({ url: '/lawyer/ai-copilot/strategy', data: { caseId }, timeout: 180000 }),
  draftDocument: async (data: { caseId: number; docType: string; title?: string }): Promise<any> =>
    request.post({ url: '/lawyer/ai-copilot/draft-document', data, timeout: 180000 }),
  result: async (caseId: number): Promise<any> =>
    request.get({ url: '/lawyer/ai-copilot/result', params: { caseId } })
}
